import { Component } from 'react'
import { cyberpunkTheme } from '../../styles/theme'

const t = cyberpunkTheme.colors

/**
 * ErrorBoundary — catches React render errors and shows a styled crash screen.
 * Prevents the whole app from going blank on unexpected JS errors.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // Log for debugging — safe to keep in production
    console.error('[ErrorBoundary]', error, info?.componentStack)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const msg = this.state.error?.message || 'An unexpected error occurred.'

    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        gap: '1.5rem',
        background: t.bg.darkest,
        textAlign: 'center',
      }}>
        <style>{`
          @keyframes eb-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>

        {/* Error icon */}
        <div style={{
          fontSize: '2rem',
          animation: 'eb-pulse 2s ease-in-out infinite',
        }}>⚠</div>

        <div>
          <h2 style={{
            margin: '0 0 0.5rem',
            fontFamily: cyberpunkTheme.fonts.display,
            fontSize: 'clamp(0.7rem, 2.5vw, 0.9rem)',
            color: t.danger,
            letterSpacing: '0.08em',
            textShadow: '0 0 12px rgba(255,0,64,0.5)',
          }}>
            SYSTEM ERROR
          </h2>
          <p style={{
            margin: 0,
            fontFamily: cyberpunkTheme.fonts.body,
            fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
            color: t.text.secondary,
            maxWidth: '420px',
            lineHeight: 1.7,
          }}>
            {msg}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              padding: '0.6rem 1.25rem',
              background: 'rgba(0,245,255,0.08)',
              border: `2px solid ${t.primary}`,
              borderRadius: '4px',
              color: t.primary,
              fontFamily: cyberpunkTheme.fonts.display,
              fontSize: '0.55rem',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              textTransform: 'uppercase',
              boxShadow: t.glow.cyan,
              minHeight: '44px',
            }}
          >
            ↻ RETRY
          </button>
          <button
            type="button"
            onClick={() => { window.location.hash = '#/'; this.setState({ hasError: false, error: null }) }}
            style={{
              padding: '0.6rem 1.25rem',
              background: 'rgba(168,85,247,0.08)',
              border: '2px solid rgba(168,85,247,0.5)',
              borderRadius: '4px',
              color: '#c084fc',
              fontFamily: cyberpunkTheme.fonts.display,
              fontSize: '0.55rem',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              textTransform: 'uppercase',
              minHeight: '44px',
            }}
          >
            ← HOME
          </button>
        </div>
      </div>
    )
  }
}
